/**
 * Authoritative 0.48-subcell island build canvas.
 *
 * Legacy SLOTS remain save/compatibility metadata. They no longer define the
 * physical limit of Build/Floor/Move/Rotate. Parcel IDs and unlock costs stay
 * the existing three-parcel economy.
 */

import { ENTRANCE, PARCEL_EAST, PARCEL_NORTH, PARCEL_START, SLOTS, slotById } from '../config/map';
import {
  FOUNDATION_DIVISION,
  createSubgridSpec,
  legacyGridToWorld,
  subcellWorldCenter,
  worldToSubgrid,
  type SubgridCoord,
  type WorldPoint,
} from './subgrid';

function legacyCellOfSubcell(coord: SubgridCoord): { col: number; row: number } {
  return {
    col: Math.floor(coord.sx / FOUNDATION_DIVISION),
    row: Math.floor(coord.sz / FOUNDATION_DIVISION),
  };
}

/**
 * Pair adjacent legacy rows into 2.4 x 4.8 plots. A raw 2.4 lattice over the
 * full island yields ~70 cells (too tedious); pairing rows keeps alignment
 * and lands in the 24–48 target.
 */
export function plotLatticeCell(col: number, row: number): { col: number; row: number } {
  return { col, row: Math.floor((row + 1) / 2) * 2 - 1 };
}

export function plotIdForLegacyCell(col: number, row: number): string {
  const lattice = plotLatticeCell(col, row);
  return `plot_${lattice.col}_${lattice.row}`;
}

export function plotIdForSubcell(coord: SubgridCoord): string {
  const cell = legacyCellOfSubcell(coord);
  return plotIdForLegacyCell(cell.col, cell.row);
}

const SPEC = createSubgridSpec(FOUNDATION_DIVISION);

/** Matches current production `islandSize()` for the accepted playtest map. */
export const PRODUCTION_ISLAND_WIDTH = 20.2;
export const PRODUCTION_ISLAND_DEPTH = 18.4;
/** Same corner radius as `createIsland` grass pad. */
export const ISLAND_CORNER_RADIUS = 1.6;
/** Small defensible inset so footprints do not overhang the rounded grass-top. */
export const ISLAND_EDGE_INSET = 0.28;

export const TICKET_BOOTH_WORLD: WorldPoint = { x: 2.85, z: 5.45 };
export const TICKET_BOOTH_HALF_WIDTH = 1.15;
export const TICKET_BOOTH_HALF_DEPTH = 0.95;

export type IslandParcelId = typeof PARCEL_START | typeof PARCEL_EAST | typeof PARCEL_NORTH;

export const COMPAT_SLOT_BY_PARCEL: Record<IslandParcelId, string> = {
  [PARCEL_START]: 'slot_a1',
  [PARCEL_EAST]: 'slot_b1',
  [PARCEL_NORTH]: 'slot_c1',
};

export interface BuildableQuery {
  coord: SubgridCoord;
  world: WorldPoint;
  insideIsland: boolean;
  insideUsableGrass: boolean;
  reserved: boolean;
  parcelId: IslandParcelId | null;
  plotId: string | null;
  unlocked: boolean;
  insideCanvas: boolean;
  buildable: boolean;
}

export interface BuildableCoverage {
  usableGrass: number;
  reserved: number;
  canvas: number;
  coverage: number;
}

function insideRoundedRect(
  x: number,
  z: number,
  halfW: number,
  halfD: number,
  radius: number,
): boolean {
  const ax = Math.abs(x);
  const az = Math.abs(z);
  if (ax > halfW || az > halfD) {
    return false;
  }
  if (ax <= halfW - radius || az <= halfD - radius) {
    return true;
  }
  const dx = ax - (halfW - radius);
  const dz = az - (halfD - radius);
  return dx * dx + dz * dz <= radius * radius + 1e-12;
}

export function productionIslandSize(): { width: number; depth: number } {
  return { width: PRODUCTION_ISLAND_WIDTH, depth: PRODUCTION_ISLAND_DEPTH };
}

export function isInsideIslandGrass(x: number, z: number): boolean {
  return insideRoundedRect(
    x,
    z,
    PRODUCTION_ISLAND_WIDTH / 2,
    PRODUCTION_ISLAND_DEPTH / 2,
    ISLAND_CORNER_RADIUS,
  );
}

export function isInsideUsableGrass(x: number, z: number): boolean {
  return insideRoundedRect(
    x,
    z,
    PRODUCTION_ISLAND_WIDTH / 2 - ISLAND_EDGE_INSET,
    PRODUCTION_ISLAND_DEPTH / 2 - ISLAND_EDGE_INSET,
    Math.max(0, ISLAND_CORNER_RADIUS - ISLAND_EDGE_INSET),
  );
}

export function isEntranceThreshold(coord: SubgridCoord): boolean {
  const cell = legacyCellOfSubcell(coord);
  return cell.col === ENTRANCE.col && cell.row === ENTRANCE.row;
}

export function isReservedInfrastructure(coord: SubgridCoord): boolean {
  if (isEntranceThreshold(coord)) {
    return true;
  }
  const world = subcellWorldCenter(coord, SPEC);
  return (
    Math.abs(world.x - TICKET_BOOTH_WORLD.x) <= TICKET_BOOTH_HALF_WIDTH &&
    Math.abs(world.z - TICKET_BOOTH_WORLD.z) <= TICKET_BOOTH_HALF_DEPTH
  );
}

export function compatibilitySlotIdForParcel(parcelId: string): string {
  if (parcelId === PARCEL_START || parcelId === PARCEL_EAST || parcelId === PARCEL_NORTH) {
    return COMPAT_SLOT_BY_PARCEL[parcelId];
  }
  return COMPAT_SLOT_BY_PARCEL[PARCEL_START];
}

export function parcelForSubcell(coord: SubgridCoord): IslandParcelId {
  const cell = legacyCellOfSubcell(coord);
  const exact = SLOTS.find((slot) => slot.col === cell.col && slot.row === cell.row);
  if (exact) {
    return exact.parcelId as IslandParcelId;
  }
  const world = subcellWorldCenter(coord, SPEC);
  let bestId: IslandParcelId = PARCEL_START;
  let bestDist = Infinity;
  for (const slot of SLOTS) {
    const center = legacyGridToWorld(slot.col, slot.row);
    const dist = Math.hypot(world.x - center.x, world.z - center.z);
    if (dist < bestDist) {
      bestDist = dist;
      bestId = slot.parcelId as IslandParcelId;
    }
  }
  return bestId;
}

export function compatibilitySlotIdForAnchor(coord: SubgridCoord): string {
  const cell = legacyCellOfSubcell(coord);
  const exact = SLOTS.find((slot) => slot.col === cell.col && slot.row === cell.row);
  if (exact) {
    return exact.id;
  }
  return compatibilitySlotIdForParcel(parcelForSubcell(coord));
}

function enumerateIslandSubcells(): SubgridCoord[] {
  const halfW = PRODUCTION_ISLAND_WIDTH / 2 + SPEC.subcellSize;
  const halfD = PRODUCTION_ISLAND_DEPTH / 2 + SPEC.subcellSize;
  const min = worldToSubgrid(-halfW, -halfD, SPEC);
  const max = worldToSubgrid(halfW, halfD, SPEC);
  const cells: SubgridCoord[] = [];
  for (let sx = min.sx; sx <= max.sx; sx += 1) {
    for (let sz = min.sz; sz <= max.sz; sz += 1) {
      cells.push({ sx, sz });
    }
  }
  return cells;
}

let cachedUsable: SubgridCoord[] | null = null;
let cachedCanvas: SubgridCoord[] | null = null;

export function usableGrassSubcells(): SubgridCoord[] {
  if (!cachedUsable) {
    cachedUsable = enumerateIslandSubcells().filter((coord) => {
      const world = subcellWorldCenter(coord, SPEC);
      return isInsideUsableGrass(world.x, world.z);
    });
  }
  return cachedUsable;
}

export function canvasSubcells(): SubgridCoord[] {
  if (!cachedCanvas) {
    cachedCanvas = usableGrassSubcells().filter((coord) => !isReservedInfrastructure(coord));
  }
  return cachedCanvas;
}

export function allParcelsUnlockedCoverage(): BuildableCoverage {
  const usable = usableGrassSubcells();
  const reserved = usable.filter((coord) => isReservedInfrastructure(coord)).length;
  const canvas = usable.length - reserved;
  return {
    usableGrass: usable.length,
    reserved,
    canvas,
    coverage: usable.length === 0 ? 0 : canvas / usable.length,
  };
}

export function queryBuildableSubcell(
  coord: SubgridCoord,
  unlockedLandIds: readonly string[],
): BuildableQuery {
  const world = subcellWorldCenter(coord, SPEC);
  const insideIsland = isInsideIslandGrass(world.x, world.z);
  const insideUsableGrass = isInsideUsableGrass(world.x, world.z);
  const reserved = insideUsableGrass && isReservedInfrastructure(coord);
  const parcelId = insideUsableGrass ? parcelForSubcell(coord) : null;
  const plotId = insideUsableGrass ? plotIdForSubcell(coord) : null;
  const unlocked = Boolean(
    plotId &&
      (unlockedLandIds.includes(plotId) ||
        (parcelId && unlockedLandIds.includes(parcelId)) ||
        (unlockedLandIds.includes(PARCEL_START) &&
          unlockedLandIds.includes(PARCEL_EAST) &&
          unlockedLandIds.includes(PARCEL_NORTH))),
  );
  const insideCanvas = insideUsableGrass && !reserved;
  return {
    coord: { sx: coord.sx, sz: coord.sz },
    world,
    insideIsland,
    insideUsableGrass,
    reserved,
    parcelId,
    plotId,
    unlocked,
    insideCanvas,
    buildable: insideCanvas && unlocked,
  };
}

export function isBuildableSubcell(coord: SubgridCoord, unlockedLandIds: readonly string[]): boolean {
  return queryBuildableSubcell(coord, unlockedLandIds).buildable;
}

export function isUnlockedPaintTargetCoord(
  coord: SubgridCoord,
  unlockedLandIds: readonly string[],
): boolean {
  const query = queryBuildableSubcell(coord, unlockedLandIds);
  if (query.insideUsableGrass && isEntranceThreshold(coord)) {
    return true;
  }
  return query.insideCanvas && query.unlocked;
}

export function unlockedCanvasSubcells(unlockedLandIds: readonly string[]): SubgridCoord[] {
  return canvasSubcells().filter((coord) => queryBuildableSubcell(coord, unlockedLandIds).unlocked);
}

export function unlockedCanvasSubcellCenters(unlockedLandIds: readonly string[]): WorldPoint[] {
  return unlockedCanvasSubcells(unlockedLandIds).map((coord) => subcellWorldCenter(coord, SPEC));
}

export function unlockedLegacyCellCenters(unlockedLandIds: readonly string[]): WorldPoint[] {
  const seen = new Set<string>();
  const centers: WorldPoint[] = [];
  for (const coord of unlockedCanvasSubcells(unlockedLandIds)) {
    const cell = legacyCellOfSubcell(coord);
    const key = `${cell.col},${cell.row}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    centers.push(legacyGridToWorld(cell.col, cell.row));
  }
  return centers;
}

export function parcelBuildableBounds(parcelId: IslandParcelId): {
  x: number;
  z: number;
  width: number;
  depth: number;
} {
  const cells = canvasSubcells().filter((coord) => parcelForSubcell(coord) === parcelId);
  const points = cells.map((coord) => subcellWorldCenter(coord, SPEC));
  const xs = points.map((point) => point.x);
  const zs = points.map((point) => point.z);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minZ = Math.min(...zs);
  const maxZ = Math.max(...zs);
  return {
    x: (minX + maxX) / 2,
    z: (minZ + maxZ) / 2,
    width: maxX - minX + SPEC.subcellSize,
    depth: maxZ - minZ + SPEC.subcellSize,
  };
}

export function knownCompatSlotId(slotId: string): boolean {
  return Boolean(slotById(slotId));
}
